import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

const AuthContext = createContext(null);
const API_URL = process.env.REACT_APP_BACKEND_URL;

function formatApiError(error) {
  const detail = error?.response?.data?.detail || error?.detail;
  if (Array.isArray(detail)) return detail.map((item) => item.msg).join(" ");
  return typeof detail === "string" ? detail : error.message || "Something went wrong.";
}

async function request(path, options = {}) {
  const response = await fetch(`${API_URL}${path}`, { credentials: "include", headers: { "Content-Type": "application/json", ...(options.headers || {}) }, ...options });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw { response: { data }, message: response.statusText };
  return data;
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    try {
      setUser(await request("/api/auth/me"));
    } catch {
      try {
        await request("/api/auth/refresh", { method: "POST" });
        setUser(await request("/api/auth/me"));
      } catch {
        setUser(false);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { checkAuth(); }, [checkAuth]);

  const value = useMemo(() => ({
    user,
    loading,
    setUser,
    login: async (payload) => setUser(await request("/api/auth/login", { method: "POST", body: JSON.stringify(payload) })),
    register: async (payload) => setUser(await request("/api/auth/register", { method: "POST", body: JSON.stringify(payload) })),
    completeGoogleSession: async (sessionId) => setUser(await request("/api/auth/google/session", { method: "POST", body: JSON.stringify({ session_id: sessionId }) })),
    logout: async () => { await request("/api/auth/logout", { method: "POST" }); setUser(false); },
    forgotPassword: async (email) => request("/api/auth/forgot-password", { method: "POST", body: JSON.stringify({ email }) }),
    resetPassword: async (payload) => request("/api/auth/reset-password", { method: "POST", body: JSON.stringify(payload) }),
    updateProfile: async (payload) => setUser(await request("/api/auth/profile", { method: "PUT", body: JSON.stringify(payload) })),
    getAccountState: async () => request("/api/account/state"),
    getOrders: async () => request("/api/account/orders"),
    getAdminOrders: async (filters = {}) => {
      const params = new URLSearchParams(Object.entries(filters).filter(([, value]) => value && value !== "all"));
      return request(`/api/payments/admin/orders${params.size ? `?${params}` : ""}`);
    },
    getAdminInsights: async () => request("/api/payments/admin/insights"),
    getAudience: async () => request("/api/auth/admin/audience"),
    getAdminAnalytics: async () => request("/api/analytics/admin/summary"),
    getFinaleList: async () => request("/api/analytics/admin/finale-list"),
    getEnquiries: async () => request("/api/analytics/admin/enquiries"),
    getMediaLibrary: async () => request("/api/media/admin/library"),
    getFanArtSubmissions: async () => request("/api/media/admin/fan-art"),
    approveFanArt: async (fileId) => request(`/api/media/admin/fan-art/${fileId}/approve`, { method: "POST" }),
    deleteMediaFile: async (fileId) => request(`/api/media/admin/${fileId}/delete`, { method: "POST" }),
    getEpisodePosters: async () => request("/api/media/episode-posters"),
    setEpisodePoster: async (episode, storagePath) => request("/api/media/admin/episode-poster", { method: "POST", body: JSON.stringify({ episode, storage_path: storagePath }) }),
    clearEpisodePoster: async (episode) => request("/api/media/admin/episode-poster/clear", { method: "POST", body: JSON.stringify({ episode }) }),
    sendEpisodeAlert: async (payload) => request("/api/auth/admin/episode-alerts", { method: "POST", body: JSON.stringify(payload) }),
    updateTracking: async (orderId, status) => request(`/api/payments/orders/${orderId}/tracking`, { method: "POST", body: JSON.stringify({ status }) }),
    addOrderNote: async (orderId, note) => request(`/api/payments/orders/${orderId}/notes`, { method: "POST", body: JSON.stringify({ note }) }),
    saveAccountState: async (payload) => request("/api/account/state", { method: "PUT", body: JSON.stringify(payload) }),
    formatApiError,
  }), [user, loading]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  return useContext(AuthContext);
}
